import { useRef } from 'react';
import { useToggle } from '@/hooks/useToggle';
import { useString } from '@/hooks/useString';
import { useAppState } from '@/providers/AppStateProvider';
import { downloadFileCollection } from '@/utils/files';
import IconButton from '@/components/IconButton';
import DefaultOptionsMenu from '@/components/DefaultOptionsMenu';
import { MenuItem } from '@/components/Menu';
import DownloadToaster from '@/toasters/DownloadToaster';
import FileCollectionDialog from '@/dialogs/FileCollectionDialog';
import DeleteFileCollectionDialog from '@/dialogs/DeleteFileCollectionDialog';
import { DownloadIcon, OptionsIcon } from '@/icons';
import { classes } from './utils';

export type CollectionOptionsMenuProps = {
  className?: string;
  fileCollectionId: number;
  onDelete?: (fileCollectionId: number) => void;
};

export default function CollectionOptionsMenu({
  className,
  fileCollectionId,
  onDelete,
}: CollectionOptionsMenuProps) {
  const menuToggleRef = useRef(null);
  const { onApiError } = useAppState();
  const [downloadMessage, setDownloadMessage] = useString(
    'Preparing collection...'
  );
  const [isMenuOpen, toggleMenu] = useToggle();
  const [isEditDialogOpen, toggleEditDialog] = useToggle();
  const [isDeleteDialogOpen, toggleDeleteDialog] = useToggle();
  const [isPreparingDownload, togglePreparingDownload] = useToggle();
  const [isDownloadToasterOpen, toggleDownloadToaster] = useToggle();

  const handleDownload = async () => {
    try {
      toggleMenu(false);
      togglePreparingDownload(true);
      toggleDownloadToaster(true);
      await downloadFileCollection(fileCollectionId, setDownloadMessage);
      togglePreparingDownload(false);
    } catch (err) {
      handleCloseDownloadToaster();
      onApiError(err, 'Error downloading collection');
    }
  };

  const handleCloseDownloadToaster = () => {
    togglePreparingDownload(false);
    toggleDownloadToaster(false);
  };

  return (
    <>
      {isEditDialogOpen && (
        <FileCollectionDialog
          fileCollectionId={fileCollectionId}
          onClose={() => toggleEditDialog(false)}
        />
      )}
      {isDeleteDialogOpen && (
        <DeleteFileCollectionDialog
          fileCollectionId={fileCollectionId}
          onDelete={onDelete}
          onClose={() => toggleDeleteDialog(false)}
        />
      )}
      <DownloadToaster
        open={isDownloadToasterOpen}
        isLoading={isPreparingDownload}
        message={downloadMessage}
        onClose={handleCloseDownloadToaster}
      />
      <IconButton
        ref={menuToggleRef}
        className={className}
        aria-label="collection options"
        size="small"
        onClick={() => toggleMenu()}>
        <OptionsIcon className={classes.optionsIcon} />
      </IconButton>
      <DefaultOptionsMenu
        anchorTo={menuToggleRef.current}
        open={isMenuOpen}
        extraOptions={
          <MenuItem
            label="Download"
            icon={<DownloadIcon fontSize="small" />}
            onClick={handleDownload}
          />
        }
        onEdit={() => toggleEditDialog(true)}
        onDelete={() => toggleDeleteDialog(true)}
        onClose={() => toggleMenu(false)}
      />
    </>
  );
}
